/*
  ba-cart-shipping-bar.js
  WHAT: Re-computes the free-shipping progress bar on cart changes (item
        add/remove/qty-change) without a full drawer reload. Reads the
        threshold from the bar's data-threshold attribute and the current
        subtotal from /cart.js. Updates the fill width, the remaining
        amount, and the unlocked-state class.
  WHY:  The first render is server-side via the Liquid snippet (correct on
        initial drawer open). Subsequent cart mutations need a JS path so
        the bar stays in sync as the customer adds items mid-session.
  WHEN: Added 2026-06-09 with the cart-drawer polish pass.

  Event coverage (belt-and-braces — Impulse uses a few different names):
    - cart:updated        (Impulse standard)
    - cart:build          (Impulse cart-drawer rebuild)
    - cart:refresh        (Impulse cart re-render)
    - ajaxProduct:added   (legacy AJAX cart add)
    - theme:cart:change   (defensive — some Impulse versions emit this)

  No storage: per CLAUDE.md rule 5, no localStorage / sessionStorage.
  All state derives from /cart.js + the data attribute on the bar.
*/

(function () {
  'use strict';

  // Defensive: if the bar isn't in the DOM, do nothing.
  function getBar() {
    return document.querySelector('[data-cart-shipping-bar]');
  }

  // AUD currency, no decimals — matches Liquid's money_without_trailing_zeros
  // filter used in the initial server-render. We strip an exact ".00" suffix
  // because Intl can still emit it in some Node-ish browsers.
  var moneyFmt = null;
  function formatMoney(cents) {
    if (!moneyFmt) {
      try {
        moneyFmt = new Intl.NumberFormat('en-AU', {
          style: 'currency',
          currency: 'AUD',
          minimumFractionDigits: 0,
          maximumFractionDigits: 0
        });
      } catch (e) {
        // Fallback if Intl unavailable.
        return '$' + Math.ceil(cents / 100);
      }
    }
    return moneyFmt.format(cents / 100).replace(/\.00$/, '');
  }

  function refresh() {
    var bar = getBar();
    if (!bar) return;

    var threshold = parseInt(bar.getAttribute('data-threshold'), 10);
    if (isNaN(threshold) || threshold <= 0) return;

    fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (data) {
        if (!data) return;
        // Re-query the bar — the drawer may have been re-rendered since
        // this fetch started.
        var liveBar = getBar();
        if (!liveBar) return;

        // Owner 2026-09-02: a gift-card-only cart must not claim free shipping - gift
        // cards never ship. Counts only; the threshold below still uses total_price, so
        // this changes visibility and nothing about the pricing rule.
        var items = data.items || [];
        var shippable = 0;
        for (var i = 0; i < items.length; i++) {
          if (items[i].requires_shipping) shippable++;
        }
        // `hidden` rather than a class: the Liquid render uses the same attribute, so the
        // server and JS states are the same thing and cannot disagree.
        liveBar.hidden = shippable === 0;
        if (shippable === 0) return;

        var subtotal = data.total_price || 0; // cents
        var remaining = threshold - subtotal;
        var pct = (subtotal * 100) / threshold;
        if (pct > 100) pct = 100;
        if (pct < 0) pct = 0;

        var fill = liveBar.querySelector('[data-ship-fill]');
        if (fill) fill.style.width = pct + '%';

        var unlocked = remaining <= 0;
        var wasUnlocked = liveBar.classList.contains('ba-cart-ship--unlocked');

        if (unlocked && !wasUnlocked) {
          // Below → at/over threshold: swap the label to the tick + small
          // caps "Free shipping unlocked".
          liveBar.classList.add('ba-cart-ship--unlocked');
          var label = liveBar.querySelector('[data-ship-label]');
          if (label) {
            label.innerHTML =
              '<svg class="ba-cart-ship__tick" aria-hidden="true" focusable="false" viewBox="0 0 16 16" width="16" height="16">' +
                '<path d="M3 8.5l3.2 3.2L13 5" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>' +
              '</svg>' +
              '<span class="ba-cart-ship__text ba-cart-ship__text--unlocked">Free shipping unlocked</span>';
          }
        } else if (!unlocked && wasUnlocked) {
          // Was unlocked, now back below — customer removed an item or
          // changed a discount. Swap back to the "Add $X" label.
          liveBar.classList.remove('ba-cart-ship--unlocked');
          var label2 = liveBar.querySelector('[data-ship-label]');
          if (label2) {
            label2.innerHTML =
              '<span class="ba-cart-ship__text">Add ' +
                '<strong data-ship-remaining>' + formatMoney(remaining) + '</strong>' +
              ' for free shipping</span>';
          }
        } else if (!unlocked) {
          // Still below threshold — update only the remaining amount.
          var remEl = liveBar.querySelector('[data-ship-remaining]');
          if (remEl) remEl.textContent = formatMoney(remaining);
        }
        // If still unlocked, nothing to update (label is static, fill is 100%).
      })
      .catch(function () { /* silent — never break the drawer */ });
  }

  // Wire up event listeners — covers the Impulse event names the existing
  // cart-drawer-stars.js uses plus the canonical Impulse `cart:build`.
  function attach() {
    document.addEventListener('cart:updated', refresh);
    document.addEventListener('cart:build', refresh);
    document.addEventListener('cart:refresh', refresh);
    document.addEventListener('ajaxProduct:added', refresh);
    document.addEventListener('theme:cart:change', refresh);
  }

  // Initial server-render from the Liquid snippet is already correct, so
  // we don't need to refresh on first load. But if the bar mounts AFTER
  // DOMContentLoaded (e.g. drawer section is re-rendered by the section
  // rendering API), kick a refresh once it's present.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', attach);
  } else {
    attach();
  }
})();
